// src/components/Inventory/MaxBid.js
import React, { useContext, useMemo } from 'react';
import { View, Pressable } from 'react-native';
import { gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import AuthContext from 'src/contexts/AuthContext';
import ConfirmDialog from 'src/elements/ConfirmDialog';
import Spinner from 'src/components/shared/Spinner';
import { Button, Notice } from 'src/elements';
import Input from 'src/elements/inputs';
import { dollars } from 'src/utils/stringHelpers';

import { CREATE_PRE_BID } from './queries';
import { SALE } from 'src/components/Bidding/queries';
import useBidSubmission from './useBidSubmission';
import {
  Container,
  TitleContainer,
  Title,
  Warning,
  SubmittingLabel,
} from './bidElements';

/**
 * Header rules:
 * - No Max Bid entered      => "Enter Max Bid"
 * - Max Bid surpassed       => "Enter New Max Bid"
 * - Max Bid not surpassed   => "Your Max Bid is $x.xx"
 */
const headerTextFor = (saleAsset) => {
  const myMax = saleAsset?.myMaxBidPrice;
  const surpassed = !!saleAsset?.myMaxBidSurpassed;

  if (!myMax) return 'Enter Max Bid';
  if (surpassed) return 'Enter New Max Bid';
  return `Your Max Bid is ${dollars(myMax)}`;
};

const SALE_ASSET_MAX_BID_FRAGMENT = gql`
  fragment SaleAssetMaxBidFields on SaleAsset {
    id
    __typename
    myMaxBidPrice
    myMaxBidSurpassed
  }
`;

const Content = ({ saleAsset, currentUser, setShow, saleId }) => {
  const asset = saleAsset.asset;

  const defaultPrice = useMemo(() => {
    if (saleAsset?.myMaxBidPrice) return String(saleAsset.myMaxBidPrice);
    if (saleAsset?.biddingNext) return String(saleAsset.biddingNext);
    if (saleAsset?.highBid) return String(parseInt(saleAsset.highBid, 10));
    return '';
  }, [saleAsset]);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      saleAssetId: saleAsset.id,
      type: 'Max',
      price: defaultPrice,
    }
  });

  const {
    requestSubmit, confirmation, confirm, cancel, submitting, error,
  } = useBidSubmission({
    mutation: CREATE_PRE_BID,
    payloadKey: 'createPreBid',
    onSuccess: () => setShow && setShow(false),
    mutationOptions: {
      refetchQueries: saleId ? [{ query: SALE, variables: { id: saleId } }] : [],
      awaitRefetchQueries: false,
      // Apollo hands `variables` to update(), so the viewer-only fields can be
      // written without a call-time update callback.
      update: (cache, { data }, { variables }) => {
        if (!data?.createPreBid?.preBid) return;

        const cacheId = cache.identify({
          __typename: 'SaleAsset', id: saleAsset.id,
        });
        if (!cacheId) return;

        cache.writeFragment({
          id: cacheId,
          fragment: SALE_ASSET_MAX_BID_FRAGMENT,
          data: {
            __typename: 'SaleAsset',
            id: saleAsset.id,
            myMaxBidPrice: variables.price,
            myMaxBidSurpassed: false,
          },
        });
      },
    },
  });

  const onSubmit = (input) => {
    const price = parseInt(input.price, 10);

    requestSubmit(
      { saleAssetId: input.saleAssetId, type: 'Max', price },
      `Confirm Max Bid of ${dollars(price)} for ${asset.name}`
    );
  };

  if (!currentUser?.buyer) {
    return (
      <Link to="/login">
        <Warning>
          Click here to log in or register in order to bid.
        </Warning>
      </Link>
    );
  } else if ('Review' === currentUser.buyer.status) {
    return (
      <Warning>
        Your account is currently being reviewed for approval to bid.
      </Warning>
    );
  } else if ('New' === currentUser.buyer.status) {
    return (
      <>
        <Warning>
          You must provide billing information before we can review
          your account.
        </Warning>
        <Link to="/account/billing">
          <Warning>
            Click Here to finish setting up your account.
          </Warning>
        </Link>
      </>
    );
  } else if (currentUser.buyer.isApproved) {
    return (
      <View>
        {error && <Notice type="error">{error}</Notice>}
        <View style={{ flexDirection: 'row', gap: 30, marginTop: 20 }}>
          <Input.Currency
            label="Your Max Bid:"
            name="price"
            control={control}
            errors={errors}
            rules={{ required: true }}
          />
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={submitting}
            style={{ maxWidth: 200 }}
          >
            {submitting ? (
              <SubmittingLabel>
                <Spinner
                  $size="16px"
                  $track="rgba(255, 255, 255, 0.4)"
                  $accent="#fff"
                />
                Submitting…
              </SubmittingLabel>
            ) : 'Submit Bid'}
          </Button>
        </View>

        <ConfirmDialog
          open={!!confirmation}
          title="Confirm Max Bid"
          message={confirmation?.message}
          confirmLabel="Place Max Bid"
          onConfirm={confirm}
          onCancel={cancel}
        />
      </View>
    );
  }

  return null;
};

const MaxBid = ({ saleAsset, setShow, saleId }) => {
  const { currentUser } = useContext(AuthContext);

  return (
    <Container>
      <TitleContainer>
        <Title>{headerTextFor(saleAsset)}</Title>
        <Pressable onPress={() => setShow(false)}>
          <Title>X</Title>
        </Pressable>
      </TitleContainer>

      <Content
        saleAsset={saleAsset}
        currentUser={currentUser}
        setShow={setShow}
        saleId={saleId}
      />
    </Container>
  );
};

export default MaxBid;
